import { collection, collectionGroup, query, where, orderBy, limit, startAfter, getDocs, doc, getDoc } from "firebase/firestore";
import { database } from "./firebase";
import { COMPLAINT_SOURCE_TYPES } from "../Utils/Constants/Constants";

// Firestore caps an "in" clause at 30 values.
const MAX_IN_CLAUSE = 30;

function toMillis(ts) {
  if (!ts) return 0;
  return ts.toMillis ? ts.toMillis() : +ts || 0;
}

// Fetches one page of real (non-internal, non-procedure) complaints, newest
// first, scoped to a single location or to a zone allowlist. Firestore can't
// combine an "issuedBy.type in [...]" clause with a "location.zone in [...]"
// clause in one query, so this runs one query per source type and merges the
// results client-side. Pass back the returned `cursors` on the next call to
// fetch the following page.
export async function fetchComplaintsPage({ locationId, allowedZones, from, to, pageSize = 20, cursors } = {}) {
  const base = collection(database, "complaints");
  const zones = Array.isArray(allowedZones) && allowedZones.length > 0
    ? allowedZones.slice(0, MAX_IN_CLAUSE)
    : null;

  const perType = await Promise.all(
    COMPLAINT_SOURCE_TYPES.map(async (type) => {
      const constraints = [where("issuedBy.type", "==", type)];
      if (locationId) {
        constraints.push(where("location.id", "==", locationId));
      } else if (zones) {
        constraints.push(where("location.zone", "in", zones));
      }
      if (from != null) constraints.push(where("createdAt", ">=", from));
      if (to != null) constraints.push(where("createdAt", "<=", to));
      constraints.push(orderBy("createdAt", "desc"));
      if (cursors?.[type]) constraints.push(startAfter(cursors[type]));
      constraints.push(limit(pageSize));

      const snap = await getDocs(query(base, ...constraints));
      return { type, docs: snap.docs };
    })
  );

  const merged = perType
    .flatMap(({ type, docs }) => docs.map((d) => ({ type, doc: d })))
    .sort((a, b) => toMillis(b.doc.data().createdAt) - toMillis(a.doc.data().createdAt));

  const page = merged.slice(0, pageSize);

  // Only advance a type's cursor past docs actually consumed into this page;
  // an unconsumed doc gets re-fetched (and re-considered) on the next page.
  const nextCursors = { ...cursors };
  perType.forEach(({ type }) => {
    const consumed = page.filter((p) => p.type === type);
    if (consumed.length > 0) {
      nextCursors[type] = consumed[consumed.length - 1].doc;
    }
  });

  const hasMore =
    perType.some(({ docs }) => docs.length === pageSize) || merged.length > page.length;

  return {
    items: page.map(({ doc: d }) => ({ documentId: d.id, ...d.data() })),
    cursors: nextCursors,
    hasMore,
  };
}

export async function fetchComplaintById(id) {
  const snap = await getDoc(doc(database, "complaints", id));
  if (!snap.exists()) return null;
  const data = { documentId: snap.id, ...snap.data() };
  return COMPLAINT_SOURCE_TYPES.includes(data.issuedBy?.type) ? data : null;
}

export async function fetchLatestProcedures(locationId, n = 10, allowedZones) {
  const base = collectionGroup(database, "procedureHistory");
  let q;
  if (locationId) {
    q = query(base, where("locationId", "==", locationId), orderBy("createdAt", "desc"), limit(n));
  } else if (Array.isArray(allowedZones) && allowedZones.length > 0) {
    q = query(base, where("location.zone", "in", allowedZones), orderBy("createdAt", "desc"), limit(n));
  } else {
    q = query(base, orderBy("createdAt", "desc"), limit(n));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ documentId: d.id, ...d.data() }));
}

export async function fetchDevicesForLocation(locationId) {
  const q = query(collection(database, "devices"), where("location.id", "==", locationId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ documentId: d.id, ...d.data() })).filter((d) => d.stream);
}

export async function fetchEmployees(allowedLocationIds) {
  let q = collection(database, "employees");
  if (Array.isArray(allowedLocationIds) && allowedLocationIds.length > 0) {
    const ids = allowedLocationIds.slice(0, 30);
    q = query(q, where("connected.location", "array-contains-any", ids));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ documentId: d.id, ...d.data() }));
}

export async function fetchEmployeeCaptures(employeeDocumentId, n = 10) {
  const q = query(
    collection(database, "captures"),
    where("employee.id", "==", employeeDocumentId),
    orderBy("createdAt", "desc"),
    limit(n)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ documentId: d.id, ...d.data() }));
}
